import React, { useCallback, useMemo } from 'react';

import { StorageKeys } from '../../constants/storage';
import { useLocalStorage } from '../../hooks';
import useMatchRouteContext from '../../hooks/useMatchRouteContext';
import useMergeProps from '../../hooks/useMergeProps';
import useMultiviewMatchContext from '../../hooks/useMultiviewMatchRouteContext';
import useTypedSelector from '../../hooks/useTypedSelector';
import { makeIsMatchLineUpAvailableWithFullDataSelector } from '../../selectors/match.selector';

import MatchToolbar from './MatchToolbar';
import selectMatchToolbarProps from './MatchToolbar.selector';

type MatchToolbarContainerProps = {
  stickyContent?: boolean;
  isStreamPaused?: boolean;
};

const MatchToolbarContainer = (ownProps: MatchToolbarContainerProps) => {
  const [hasLineUpTooltipBeenViewed, setHasLineUpTooltipBeenViewed] = useLocalStorage(
    StorageKeys.HAS_LINE_UP_TOOLTIP_BEEN_VIEWED,
    false,
  );
  const [hasEventCenterTooltipBeenViewed, setHasEventCenterTooltipBeenViewed] = useLocalStorage(
    StorageKeys.HAS_EVENT_CENTER_TOOLTIP_BEEN_VIEWED,
    false,
  );

  const { matchId: matchIdFromMatchRoute } = useMatchRouteContext();
  const { matchId: matchIdFromMultiview } = useMultiviewMatchContext();

  const onCloseLineUpTooltip = useCallback(() => {
    setHasLineUpTooltipBeenViewed(true);
  }, [setHasLineUpTooltipBeenViewed]);

  const onCloseEventCenterTooltip = useCallback(() => {
    setHasEventCenterTooltipBeenViewed(true);
  }, [setHasEventCenterTooltipBeenViewed]);

  const matchId = useMemo(
    () => matchIdFromMatchRoute ?? matchIdFromMultiview,
    [matchIdFromMatchRoute, matchIdFromMultiview],
  );

  const selectorProps = useMemo(() => ({ matchId }), [matchId]);
  const selectedProps = useTypedSelector((state) => selectMatchToolbarProps(state, selectorProps));
  const selectMatchLineUpIsPartiallyAvailable = makeIsMatchLineUpAvailableWithFullDataSelector();
  const isLineUpAvailable = useTypedSelector((state) => selectMatchLineUpIsPartiallyAvailable(state, selectorProps));

  const otherProps = useMemo(
    () => ({
      showLineUpTooltip: !hasLineUpTooltipBeenViewed,
      onCloseLineUpTooltip,
      isLineUpAvailable,
      showEventCenterTooltip: !hasEventCenterTooltipBeenViewed,
      onCloseEventCenterTooltip,
    }),
    [
      hasLineUpTooltipBeenViewed,
      onCloseLineUpTooltip,
      isLineUpAvailable,
      hasEventCenterTooltipBeenViewed,
      onCloseEventCenterTooltip,
    ],
  );

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });
  const { matchId: enhancedMatchId, sportId, categoryId, options, periodId } = enhancedProps;
  const shouldRender =
    typeof enhancedMatchId !== 'undefined' &&
    typeof sportId !== 'undefined' &&
    typeof categoryId !== 'undefined' &&
    typeof options !== 'undefined';

  return shouldRender ? (
    <MatchToolbar
      {...enhancedProps}
      matchId={enhancedMatchId}
      sportId={sportId}
      categoryId={categoryId}
      options={options}
      periodId={periodId}
    />
  ) : null;
};

export default MatchToolbarContainer;
